// Thin fetch helpers for the /api/admin endpoints.
//
// The project has no fetch wrapper: every call is a raw fetch with
// credentials: 'include' and a relative path. These keep that convention but
// centralise the error handling, because the admin console has enough
// endpoints that repeating it per component would be the bigger sin.

const jsonHeaders = { 'Content-Type': 'application/json' };

async function readJson(response) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch (error) {
    throw new Error(`Unexpected response from the server (${response.status})`);
  }
}

async function request(path, options = {}) {
  const response = await fetch(path, {
    credentials: 'include',
    ...options,
  });
  const data = await readJson(response);
  if (!response.ok) {
    // 403 means the signed-in account is not on the ADMIN_EMAILS allowlist.
    throw new Error(data.error || `Request failed (${response.status})`);
  }
  return data;
}

function query(params) {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, value);
    }
  });
  const encoded = search.toString();
  return encoded ? `?${encoded}` : '';
}

export function fetchMeta(signal) {
  return request('/api/admin/meta', { signal });
}

export function searchCards(params, signal) {
  return request(`/api/admin/cards/search${query(params)}`, { signal });
}

export function fetchCardDetail(cardId, signal) {
  return request(`/api/admin/cards/${encodeURIComponent(cardId)}`, { signal });
}

export function fetchExclusions(params, signal) {
  return request(`/api/admin/exclusions${query(params)}`, { signal });
}

export function createExclusion(cardId, reason) {
  return request('/api/admin/exclusions', {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify({ card_id: cardId, reason }),
  });
}

export function restoreExclusion(exclusionId) {
  return request(`/api/admin/exclusions/${exclusionId}/restore`, { method: 'POST' });
}

export function fetchUsers(params, signal) {
  return request(`/api/admin/users${query(params)}`, { signal });
}

export function updateUser(userId, changes) {
  return request(`/api/admin/users/${userId}`, {
    method: 'PATCH',
    headers: jsonHeaders,
    body: JSON.stringify(changes),
  });
}

export function fetchScriptRuns(params, signal) {
  return request(`/api/admin/script-runs${query(params)}`, { signal });
}
